/** Pure compute helpers for flat-list drag reorder (orderKey only, no parent change). */
import { generateKeyBetween } from 'fractional-indexing';
import { reorderByActiveOver } from '@/features/today/utils/todayListReorderCompute';

/**
 * @param {string} taskId
 * @param {string} ancestorId
 * @param {Map<string, { parentId?: string | null }>} tasksMap
 */
export const isDescendantOf = (taskId, ancestorId, tasksMap) => {
  let current = tasksMap.get(String(taskId));
  while (current?.parentId) {
    if (current.parentId === ancestorId) return true;
    current = tasksMap.get(String(current.parentId));
  }
  return false;
};

/**
 * @param {string} taskId
 * @param {string} descendantId
 * @param {Map<string, { parentId?: string | null }>} tasksMap
 */
export const isAncestorOf = (taskId, descendantId, tasksMap) =>
  isDescendantOf(descendantId, taskId, tasksMap);

/**
 * Computes a new orderKey when `activeId` is dropped over `overId` in a flat
 * SortableContext list. Does not change parentId — ancestor/descendant drops
 * are rejected so drag never outdents/indents via reorder alone.
 *
 * @param {Array<{ id: string, orderKey?: string }>} reorderList
 * @param {string} activeId
 * @param {string} overId
 * @param {{ id: string, orderKey?: string }} activeTask
 * @param {Map<string, { parentId?: string | null }>} [tasksMap]
 * @returns {string | null}
 */
export const computeFlatListDropOrderKey = (
  reorderList,
  activeId,
  overId,
  activeTask,
  tasksMap = null,
) => {
  if (!activeId || !overId || activeId === overId) return null;

  if (tasksMap) {
    if (isAncestorOf(activeId, overId, tasksMap)) return null;
    if (isDescendantOf(activeId, overId, tasksMap)) return null;
  }

  const result = reorderByActiveOver(reorderList, activeId, overId);
  if (!result) return null;

  const { order, newIndex } = result;
  const prev = order[newIndex - 1];
  const next = order[newIndex + 1];
  const newOrderKey = generateKeyBetween(
    prev?.orderKey ?? null,
    next?.orderKey ?? null,
  );
  if (newOrderKey === activeTask.orderKey) return null;
  return newOrderKey;
};

/**
 * Expected materializedPath after an orderKey-only move (parent unchanged).
 * @param {{ parentId?: string | null, materializedPath?: string }} task
 * @param {string} newOrderKey
 * @param {Map<string, { materializedPath?: string, orderKey?: string }>} tasksMap
 */
export const expectedMaterializedPathAfterOrderKeyMove = (
  task,
  newOrderKey,
  tasksMap,
) => {
  const parentPath = task.parentId
    ? tasksMap.get(String(task.parentId))?.materializedPath || ''
    : '';
  return `${parentPath}${newOrderKey}.`;
};
